import React from 'react';
import PropTypes from 'prop-types';

import { appealTypes } from '../constants';

import AppealContent from './AppealContent';
import ClaimContent from './ClaimContent';

import '../sass/ClaimOrAppealCard.scss';

/**
 * Shared claim or appeal card for the My VA / CST card alignment work (#141141).
 *
 * Renders a single benefits item (claim or appeal) using CST's existing visual
 * design. Detects the item type internally and renders the appropriate layout.
 *
 * Styles: this component imports its own stylesheet from
 * `../sass/ClaimOrAppealCard.scss`. Because that SCSS import is not
 * understood by the Cypress test bundler, the component is intentionally
 * NOT re-exported from `platform/shared/claims-appeals/index.js`. Import
 * it via the direct path:
 *   import { ClaimOrAppealCard } from '~/platform/shared/claims-appeals/components/ClaimOrAppealCard';
 *
 * Routing is intentionally NOT handled here. The card is router-agnostic and
 * renders the Details link as a plain full-page navigation unless the consumer
 * passes an `onLinkClick` handler.
 *
 * @param {Object} props.item - claim or appeal object
 * @param {(event: MouseEvent, href: string) => void} [props.onLinkClick] - Details link click handler for SPA navigation
 */
export function ClaimOrAppealCard({ item, onLinkClick }) {
  if (!item?.attributes) return null;

  const isAppealType = appealTypes.includes(item.type);

  return (
    <va-card class="claim-list-item" data-testid="claim-or-appeal-card">
      {isAppealType ? (
        <AppealContent appeal={item} onLinkClick={onLinkClick} />
      ) : (
        <ClaimContent claim={item} onLinkClick={onLinkClick} />
      )}
    </va-card>
  );
}

ClaimOrAppealCard.propTypes = {
  item: PropTypes.shape({
    id: PropTypes.string,
    type: PropTypes.string,
    attributes: PropTypes.object,
  }).isRequired,
  onLinkClick: PropTypes.func,
};
